import React, { useEffect, useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../socket";
import brandLogo from "../TimeEdgeIcon.png";
import "../styles.css";
import Header from "./Header";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

// Route: /reset-password?token=xxx&email=user@krivamail.com
function ResetPasswordPage() {
  // Read params from URL
  const params = new URLSearchParams(window.location.search);
  const token  = params.get("token")  || "";
  const email  = (params.get("email") || "").toLowerCase();

  // status: "validating" | "valid" | "invalid" | "success"
  const [status,          setStatus]          = useState("validating");
  const [errorMsg,        setErrorMsg]         = useState("");
  const [newPassword,     setNewPassword]      = useState("");
  const [confirmPassword, setConfirmPassword]  = useState("");
  const [showPwd,         setShowPwd]          = useState(false);
  const [showConfirm,     setShowConfirm]      = useState(false);
  const [loading,         setLoading]          = useState(false);
  const [formMsg,         setFormMsg]          = useState("");

  // Verify token with server on mount
  useEffect(() => {
    if (!token || !email) {
      setStatus("invalid");
      setErrorMsg("Invalid or missing reset link. Please request a new one.");
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/auth/verify-reset-token`, {
          params: { token, email },
        });
        if (res.data?.valid) {
          setStatus("valid");
        } else {
          setStatus("invalid");
          setErrorMsg(res.data?.error || "This reset link is invalid or has expired.");
        }
      } catch (err) {
        setStatus("invalid");
        setErrorMsg(
          err.response?.data?.error ||
          "Could not validate reset link. Please request a new one."
        );
      }
    };

    verify();
  }, []); // run once on mount

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMsg("");

    if (newPassword.length < 6) {
      setFormMsg("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormMsg("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${SERVER_URL}/api/auth/reset-password`, {
        token,
        email,
        newPassword,
      });
      if (res.data?.success) {
        setStatus("success");
      } else {
        setFormMsg(res.data?.error || "Failed to reset password.");
      }
    } catch (err) {
      setFormMsg(err.response?.data?.error || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="login-page">
        <div className="login-layout" style={{ justifyContent: "center" }}>
          <div className="login-card" style={{ maxWidth: 440 }}>

            <center>
              <img src={brandLogo} className="imglogo" alt="Brand Logo" />
            </center>

            {/* Validating */}
            {status === "validating" && (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                <p>Validating reset link…</p>
              </div>
            )}

            {/* Invalid / expired */}
            {status === "invalid" && (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                <h2 className="login-title">Link Invalid</h2>
                <p style={{ color: "#ef4444", marginBottom: 20 }}>{errorMsg}</p>
                <a
                  href="/login"
                  className="btn-primary"
                  style={{ display: "inline-block", textDecoration: "none" }}
                >
                  Back to Login
                </a>
              </div>
            )}

            {/* Valid — show form */}
            {status === "valid" && (
              <>
                <h2 className="login-title">Set New Password</h2>
                <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>
                  Setting new password for <strong>{email}</strong>
                </p>

                {formMsg && <div className="login-error">{formMsg}</div>}

                <form onSubmit={handleSubmit} className="login-form">
                  <label>New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPwd ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      autoFocus
                    />
                    <span
                      className="password-toggle"
                      onClick={() => setShowPwd((p) => !p)}
                    >
                      {showPwd
                        ? <AiOutlineEyeInvisible size={18} />
                        : <AiOutlineEye size={18} />}
                    </span>
                  </div>

                  <label style={{ marginTop: 12 }}>Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <span
                      className="password-toggle"
                      onClick={() => setShowConfirm((p) => !p)}
                    >
                      {showConfirm
                        ? <AiOutlineEyeInvisible size={18} />
                        : <AiOutlineEye size={18} />}
                    </span>
                  </div>

                  <div className="login-actions" style={{ marginTop: 20 }}>
                    <button type="submit" disabled={loading}>
                      {loading ? "Saving…" : "Reset Password"}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* Success */}
            {status === "success" && (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div className="success-box">
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                  <h3>Password Reset!</h3>
                  <p>Your password has been updated successfully.</p>
                </div>
                <a
                  href="/login"
                  className="btn-primary"
                  style={{
                    display: "inline-block",
                    marginTop: 20,
                    textDecoration: "none",
                  }}
                >
                  Go to Login
                </a>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

export default ResetPasswordPage;
